import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Warning } from '~/assets/svg/warning.svg';
import { DELETE_AGENT, UPDATE_MANUAL_MODE } from '~/services';
import { ReactComponent as Whatsapp } from '~/assets/svg/whatsapp.svg';
import { ReactComponent as Instagram } from '~/assets/svg/instagram.svg';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '~/components/contexts/SessionProvider';

const TurnOffAgent = ({ channels: incomingChannels }: any) => {
  const [t] = useTranslation('advance_settings');
  const [turnOffBot] = useMutation(UPDATE_MANUAL_MODE);

  const [channels, setChannels] = useState(incomingChannels);

  const onSwitch = async (channel_id: string, value: boolean) => {
    await turnOffBot({
      variables: {
        data: {
          id: channel_id,
          state: !value,
          type: 'CHANNEL',
        },
      },
      onCompleted: () => {
        setChannels((prev: any) =>
          prev.map((channel: any) =>
            channel._id === channel_id
              ? { ...channel, manual_mode: !value }
              : channel,
          ),
        );
      },
    });
  };

  useEffect(() => {
    setChannels(incomingChannels);
  }, [incomingChannels]);

  if (channels.length === 0) {
    return null;
  }

  const Modal = () => (
    <dialog id="turn_off_modal" className="modal">
      <div className="modal-box">
        <h3 className="mb-8 flex gap-2 text-lg font-bold">
          {t('danger.turn_off.modal_title')}
        </h3>
        {channels.map((channel: any) => (
          <div
            key={channel._id}
            className="mt-4 flex items-center justify-between gap-4"
          >
            {channel.messaging_product === 'whatsapp' ? (
              <div className="flex flex-row gap-2">
                <Whatsapp className="w-4 text-current" />
                <p className="text-sm">{t('danger.turn_off.whatsapp')}</p>
              </div>
            ) : (
              <div className="flex flex-row gap-2">
                <Instagram className="w-4 text-current" />
                <p className="text-sm">{t('danger.turn_off.instagram')}</p>
              </div>
            )}
            <input
              onChange={(e) => onSwitch(channel._id, e.target.checked)}
              type="checkbox"
              className="toggle toggle-success"
              defaultChecked
              checked={!channel.manual_mode}
            />
          </div>
        ))}
        <div className="modal-action">
          <form method="dialog">
            <button className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2">
              ✕
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );

  return (
    <div className="my-8">
      <h3 className="mb-4 mt-2 flex items-center gap-3 text-lg font-medium">
        {t('danger.turn_off.title')}
        {channels.some((channel: any) => channel.manual_mode) ? (
          <div className="bg-warning h-2 w-2 flex-shrink-0 rounded-full"></div>
        ) : null}
      </h3>
      <div className="mb-4 mt-2 flex flex-col items-center justify-between gap-10 md:flex-row">
        <p className="text text-sm">{t('danger.turn_off.description')}</p>

        <button
          className="btn btn-primary w-48 rounded-md"
          onClick={() =>
            (
              document.getElementById('turn_off_modal') as HTMLDialogElement
            )?.showModal()
          }
        >
          {t('danger.turn_off.title')}
        </button>
      </div>
      <Modal />
    </div>
  );
};

const DeleteAgent = ({ agentId }: { agentId: string }) => {
  const [t] = useTranslation('advance_settings');
  const { getOwner, owner } = useSession();
  const navigate = useNavigate();
  const [deleteAgent] = useMutation(DELETE_AGENT, {
    onCompleted: () => {
      navigate(`/agents/${owner?.agents[0]._id}`);
      getOwner();
    },
  });

  const Modal = () => (
    <dialog id="delete_agent_modal" className="modal">
      <div className="modal-box">
        <h3 className="mb-8 text-lg font-bold">
          {t('danger.delete.modal.title')}
        </h3>
        <p className="text-sm">{t('danger.delete.modal.description')}</p>
        <div className="mt-6 flex gap-4">
          <button
            className="btn btn-primary w-48 rounded-md"
            onClick={() => {
              deleteAgent({
                variables: {
                  data: {
                    id: agentId,
                  },
                },
              });
            }}
          >
            {t('danger.delete.modal.accept')}
          </button>
          <button
            className="btn btn-primary w-48 rounded-md"
            onClick={() =>
              (
                document.getElementById(
                  'delete_agent_modal',
                ) as HTMLDialogElement
              ).close()
            }
          >
            {t('danger.delete.modal.cancel')}
          </button>
        </div>
        <div className="modal-action">
          <form method="dialog">
            <button className="btn btn-ghost btn-sm btn-circle absolute right-2 top-2">
              ✕
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );

  return (
    <div className="my-8">
      <h3 className="mb-4 mt-2 text-lg font-medium">
        {t('danger.delete.title')}
      </h3>
      <div className="mb-4 mt-2 flex flex-col items-center gap-10 md:flex-row">
        <p className="text-sm">{t('danger.delete.description')}</p>
        <button
          className="btn btn-primary w-48 rounded-md"
          onClick={() =>
            (
              document.getElementById('delete_agent_modal') as HTMLDialogElement
            )?.showModal()
          }
        >
          {t('danger.delete.title')}
        </button>
      </div>
      <Modal />
    </div>
  );
};

export const DangerZone = ({
  channels,
  agentId,
}: {
  channels: any[];
  agentId: string;
}) => {
  const [t] = useTranslation('advance_settings');

  return (
    <div className="collapse-arrow bg-custom-surface-50 collapse ">
      <input type="checkbox" />
      <div className="collapse-title flex gap-2 text-xl font-medium">
        <Warning className="w-5 text-current" />
        {t('danger.title')}
      </div>
      <div className="collapse-content">
        <TurnOffAgent channels={channels} />
        <DeleteAgent agentId={agentId} />
      </div>
    </div>
  );
};
